import React, { ReactNode } from 'react';
import * as S from './ProfileLayout.styled';
import { useDesktopProfileScroll } from './ProfileLayout.hooks';
import dynamic from 'next/dynamic';
import { Footer } from '@/components/Footer/Footer';
import { Header } from '@/components/Header';
import { PageTopControls } from '@/components/PageTopControls';
import { TabBar } from '@/components/TabBar/TabBar';
import { Toast } from '@/components/UI/Toast';
import { MobileProfileHeader } from '@/features/Profile/core/MobileProfileHeader';
import { MobileProfileOverview } from '@/features/Profile/core/MobileProfileOverview';
import { ProfileNav } from '@/features/Profile/core/ProfileNav';
import { UserSummaryCard } from '@/features/Profile/core/UserSummaryCard';
import { useResponsive } from '@/hooks/useResponsive';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';
import { WebViewPaymentStatusModal } from '@/components/Payment/WebViewPaymentStatusModal';
import { useSelectionModeStore } from '@/store/useSelectionModeStore';
import { MY_ADS_PATHS } from '@/features/Profile/core/ProfileNav/ProfileNav.constants';
import { useInvoicePromotion } from '@/features/Profile/sections/Packages/hooks';
import { InvoicePromotionBanner } from '@/features/Profile/sections/Packages/components/InvoicePromotionBanner';

type ProfileLayoutProps = {
  children: ReactNode;
  isContentFramed?: boolean;
  mobileHeader?: {
    title: string;
    hideTitle?: boolean;
  };
  hideFooter?: boolean;
};

const WelcomeBanner = dynamic(
  () =>
    import('@/components/ReplenishmentWelcomeBanner').then(m => ({
      default: m.WelcomeBanner,
    })),
  { ssr: false }
);

export const ProfileLayout = ({
  children,
  isContentFramed = true,
  mobileHeader,
  hideFooter = false,
}: ProfileLayoutProps) => {
  const { isDesktop } = useResponsive();
  const { clearUrl: currentPath } = useUrlAndHashFromPath();
  const { sidebarRef } = useDesktopProfileScroll(isDesktop);
  const isSelectionModeActive = useSelectionModeStore(state => state.isActive);
  const isMyAdsPage = MY_ADS_PATHS.includes(currentPath);
  const { promotion } = useInvoicePromotion(isDesktop || isMyAdsPage);
  const mobilePromotion = isMyAdsPage ? promotion : null;

  return (
    <S.Page>
      <WelcomeBanner />
      {isDesktop && (
        <PageTopControls>
          <Header />
        </PageTopControls>
      )}

      {!isDesktop &&
        !isSelectionModeActive &&
        (mobileHeader ? (
          <MobileProfileHeader
            title={mobileHeader.title}
            hideTitle={mobileHeader.hideTitle}
            currentPath={currentPath}
          />
        ) : (
          <MobileProfileOverview
            currentPath={currentPath}
            promotion={mobilePromotion}
          />
        ))}

      <S.Main>
        {isDesktop && (
          <S.Sidebar ref={sidebarRef}>
            <UserSummaryCard currentPath={currentPath} />
            <ProfileNav currentPath={currentPath} />
          </S.Sidebar>
        )}

        <S.MainColumn>
          {isDesktop && promotion && (
            <InvoicePromotionBanner promotion={promotion} />
          )}

          <S.Content
            $isFramed={isContentFramed}
            $hasCompactHeader={Boolean(mobileHeader)}
          >
            {children}
          </S.Content>
        </S.MainColumn>
      </S.Main>

      {isDesktop ? (
        <S.DesktopFooter hasRegions={false} />
      ) : (
        !hideFooter && <Footer />
      )}
      {!isDesktop && !hideFooter && !isSelectionModeActive && <TabBar />}

      <WebViewPaymentStatusModal />
      <Toast />
    </S.Page>
  );
};
